import { Box, Typography } from '@mui/material'
import { Field, ErrorMessage } from 'formik'
import { format, parse, eachDayOfInterval } from 'date-fns'

import { useGetDisabledDate } from 'services/api/modules/leave/calendar'

import getWeekValue from 'module/leave/util/getWeekValue'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxDateRangePicker from 'components/reusable/AdxDateRangePicker'
import AdxInput from 'components/custom/AdxInput'
import AdxDropzoneImagePdf from 'components/reusable/AdxDropzoneImagePdf'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import { useEffect } from 'react'

const FillLeaveWithoutEntitlement = ({
  selectedLeave,
  values,
  handleChange,
  handleBlur,
  setFieldValue,
  errors,
  setFieldTouched,
  touched,
  validateField,
  requestDateError,
  setDisableNextButton,
}) => {
  const { data: disabledCalendarData } = useGetDisabledDate(selectedLeave.leave_policy_id)
  const ph = disabledCalendarData?.data?.holiday?.public_holiday?.length
    ? disabledCalendarData?.data?.holiday?.public_holiday?.map((day) =>
        format(parse(day.date, `dd/MM/yyyy`, new Date()), `dd MMMM yyyy`)
      )
    : []
  const ch = disabledCalendarData?.data?.holiday?.custom_holiday?.length
    ? disabledCalendarData?.data?.holiday?.custom_holiday?.map((day) =>
        format(parse(day.date, `dd/MM/yyyy`, new Date()), `dd MMMM yyyy`)
      )
    : []
  const takenLeave = disabledCalendarData?.data?.leave?.length
    ? disabledCalendarData?.data?.leave?.map((day) =>
        format(parse(day.date, `yyyy-MM-dd`, new Date()), `dd MMMM yyyy`)
      )
    : []

  const quota = disabledCalendarData?.data?.full_quota?.length
    ? disabledCalendarData?.data?.full_quota?.map((day) =>
        format(parse(day.date, `yyyy-MM-dd`, new Date()), `dd MMMM yyyy`)
      )
    : []
  const offDay = disabledCalendarData?.data?.workday?.off_day?.map((day) => getWeekValue(day.day))
  const restDay = disabledCalendarData?.data?.workday?.rest_day?.map((day) => getWeekValue(day.day))

  const leavePolicy = selectedLeave?.leave_policy

  // date conflicts comparison function
  const selectedDates =
    values.startDate && values.endDate
      ? eachDayOfInterval({
          start: parse(values.startDate, 'dd/MM/yyyy', new Date()),
          end: parse(values.endDate, 'dd/MM/yyyy', new Date()),
        }).map((date) => format(date, 'dd MMMM yyyy'))
      : []

  const getConflictingDates = (selectedDates, takenLeave) => {
    return selectedDates.filter((date) => takenLeave.includes(date))
  }

  const hasDateConflict = selectedDates.some((date) => takenLeave.includes(date))
  const conflictingDates = getConflictingDates(selectedDates, takenLeave)

  useEffect(() => {
    if (hasDateConflict === true) setDisableNextButton(true)
    else setDisableNextButton(false)
  }, [hasDateConflict])

  return (
    <>
      {/* Shift and select date form */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', tablet: 'row' },
            pt: 1,
            pb: 2,
            gap: 2,
          }}
        >
          <Field
            as={AdxDropdown}
            name="shiftLeave"
            disabled={selectedLeave.leave_policy.half_day_option ? false : true}
            value={values.shiftLeave}
            onChange={handleChange}
            onBlur={handleBlur}
            label={'Shift'}
            sx={{ width: { xs: '100%', tablet: '40%' } }}
          >
            <AdxMenuItem selectedvalue={values.shiftLeave} value={'Full Day'}>
              Full day
            </AdxMenuItem>
            <AdxMenuItem selectedvalue={values.shiftLeave} value={'AM'}>
              Half day (AM)
            </AdxMenuItem>
            <AdxMenuItem selectedvalue={values.shiftLeave} value={'PM'}>
              Half day (PM)
            </AdxMenuItem>
          </Field>
          <Field
            as={AdxDateRangePicker}
            disabledDates={
              leavePolicy.include_non_working_days === 0 && disabledCalendarData
                ? [...ph, ...ch, ...takenLeave, ...quota]
                : []
            }
            disableCertainDays={
              leavePolicy.include_non_working_days === 0 && disabledCalendarData
                ? [...offDay, ...restDay]
                : []
            }
            minDayFromToday={selectedLeave.leave_policy.day_prior}
            disablePast={selectedLeave.leave_policy.day_prior}
            restrictions={
              values.shiftLeave === 'AM' || values.shiftLeave === 'PM'
                ? 1
                : selectedLeave.leave_policy.restriction_amount
            }
            startCycle={new Date(selectedLeave.cycle_start_date)}
            endCycle={new Date(selectedLeave.cycle_end_date)}
            values={{
              startDate: values.startDate,
              endDate: values.endDate,
              duration: values.duration,
            }}
            setValue={(e) => {
              setFieldValue('startDate', e.startDate)
              setFieldValue('endDate', e.endDate)
              setFieldValue('duration', e.duration)
            }}
            label={'Date'}
            name="startDate"
            sx={{ width: '60%' }}
            errorMessage={
              requestDateError ? (
                <AdxErrorMessage>{requestDateError}</AdxErrorMessage>
              ) : (
                <ErrorMessage name="startDate" component={AdxErrorMessage} />
              )
            }
            setFieldTouched={setFieldTouched}
            validateField={validateField}
            isErrorShown={requestDateError ? true : errors?.startDate && touched?.startDate}
          />
        </Box>

        {hasDateConflict && (
          <Box
            sx={{
              bgcolor: 'error.light',
              border: 1,
              borderRadius: '4.5px',
              borderColor: 'error.main',
              p: 3,
            }}
          >
            <Typography>
              Your selected date range appears to be conflicting with other leave request.
            </Typography>
            <ul>
              {conflictingDates?.map((date, index) => (
                <li>
                  <Typography>{date}</Typography>
                </li>
              ))}
            </ul>
            <Typography>
              Make sure to withdraw any other affected leave application coincide with the dates
              before applying new leave request.
            </Typography>
          </Box>
        )}

        {/* Entitlement Restriction box */}
        {leavePolicy?.day_prior || leavePolicy?.quota_amount || leavePolicy?.restriction_amount ? (
          <Box
            sx={{
              backgroundColor: 'gray-background.main',
              borderRadius: 1.25,
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              rowGap: { xs: 1.5, sm: 1 },
              mb: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body1" color="error.main">
                Restrictions
              </Typography>
            </Box>
            {leavePolicy?.quota_amount && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { xs: null, sm: 'space-between' },
                }}
              >
                <Typography variant="body1" color="light-gray-text.main">
                  Maximum Number of Employees Quota
                </Typography>

                <Typography variant="body1" color="dark-text.main">
                  {leavePolicy?.quota_amount}{' '}
                  {leavePolicy?.quota_unit === 'percent'
                    ? leavePolicy?.quota_unit
                    : leavePolicy?.quota_amount > 1
                    ? 'persons'
                    : 'person'}{' '}
                  {leavePolicy?.quota_category === 'company' ? 'of the' : 'per'}{' '}
                  {leavePolicy?.quota_category}
                </Typography>
              </Box>
            )}
            {leavePolicy?.restriction_amount && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { xs: null, sm: 'space-between' },
                }}
              >
                <Typography variant="body1" color="light-gray-text.main">
                  Number of Consecutive Days Allowed
                </Typography>

                <Typography variant="body1" color="dark-text.main">
                  {leavePolicy?.restriction_amount} days in a row
                </Typography>
              </Box>
            )}

            {leavePolicy?.day_prior && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: { xs: null, sm: 'space-between' },
                }}
              >
                <Typography variant="body1" color="light-gray-text.main">
                  Number of Days Prior to Application
                </Typography>

                <Typography variant="body1" color="dark-text.main">
                  {leavePolicy?.day_prior} {leavePolicy?.day_prior > 1 ? 'days' : 'day'}
                </Typography>
              </Box>
            )}
          </Box>
        ) : null}
        {/* Entitlement details box */}
        <Box
          sx={{
            backgroundColor: 'gray-background.main',
            borderRadius: 1.25,
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
            rowGap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: null, sm: 'space-between' },
            }}
          >
            <Typography variant="body1" color="light-gray-text.main">
              Days to be requested
            </Typography>
            <Typography variant="body1" color="dark-text.main">
              {values.duration} {values.duration > 1 ? 'days' : 'day'}
            </Typography>
          </Box>
          {errors?.duration && (
            <Typography variant="caption" color="error.main">
              {errors?.duration}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Multine input for reasons / remarks */}
      <Field
        as={AdxInput}
        label={`Leave purpose ${
          selectedLeave.leave_policy.reason_required ? '(Required)' : '(Optional)'
        } `}
        multiline
        minRows={4}
        name="reason"
        value={values.reason}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Enter reason here..."
        charLimit={120}
        sxInput={{ p: 2 }}
        errorMessage={<ErrorMessage name="reason" component={AdxErrorMessage} />}
        isErrorShown={errors.reason && touched.reason}
      />
      {/* Dropzone to upload documents */}
      <Field name="file" values={values.file} type="hidden" />
      <AdxDropzoneImagePdf
        file={
          Object.keys(values.file).length !== 0
            ? { name: values?.file?.name, url: values?.file?.awsUrl }
            : null
        }
        label={`Attachment
            ${selectedLeave.leave_policy.attachment_required ? ' (Required)' : ' (Optional)'}`}
        title="Drag an attachment from computer"
        subtitle="or click here to upload"
        folder="leave_attachment"
        setFieldValue={setFieldValue}
        errorMessage={<ErrorMessage name="file" component={AdxErrorMessage} />}
        isErrorShown={errors.file && touched.file}
      />
    </>
  )
}

export default FillLeaveWithoutEntitlement
